import { withErrorBoundary } from "@sentry/nextjs";
import cn from "classnames";
import { FC, useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { SupTitle } from "components";
import { FullArrow } from "icons";
import { Carousel2Content } from "types/graphql/generated";
import { Card } from "./Card";

interface Props {
  className?: string;
  content: Carousel2Content;
}

const Carousel2Desktop: FC<Props> = withErrorBoundary(({ className = "", content: { title, carousel2Options } }) => {
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);

  const createHandleToggleActiveOptionIndex = useCallback(
    (name: string) => () => {
      const activeOptionIndex = carousel2Options.findIndex((option) => option.optionName === name);

      setActiveOptionIndex(activeOptionIndex);
    },
    [carousel2Options]
  );

  const activeOption = useMemo(() => {
    return carousel2Options[activeOptionIndex];
  }, [carousel2Options, activeOptionIndex]);

  return (
    <StyledCarousel2Desktop className={className}>
      <div className="Carousel2__tabButtonsWithTitleContainer">
        <SupTitle>{title}</SupTitle>
        <div className="Carousel2__tabButtons">
          {carousel2Options.map(({ id, optionName }) => (
            <button
              key={id}
              onClick={createHandleToggleActiveOptionIndex(optionName)}
              className={cn("Carousel2__tabButton Font_40_48_600 xl:Font_32_40_600 md:Font_24_32_400", {
                Carousel2__tabButtonActive: optionName === activeOption.optionName,
                Color_tertiary: optionName !== activeOption.optionName,
              })}
            >
              {optionName}
              <FullArrow className="Carousel__arrowIcon" />
            </button>
          ))}
        </div>
      </div>

      <ul className="Carousel2__tabs">
        {carousel2Options.map((option, index) => (
          <Card
            key={option.optionName}
            className={cn("Carousel2__tab", {
              Carousel2__tab_active: index === activeOptionIndex,
            })}
            content={option}
          />
        ))}
      </ul>
    </StyledCarousel2Desktop>
  );
}, {});

const StyledCarousel2Desktop = styled.div`
  display: flex;
  justify-content: space-between;

  .Carousel2__tabButtonsWithTitleContainer {
    max-width: 450px;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
  }

  .Carousel2__tabButtons {
    display: flex;
    flex-direction: column;
    margin-top: 48px;
  }

  .Carousel__arrowIcon {
    position: relative;
    width: 36px;
    height: 36px;
    left: 0;
    flex-shrink: 0;
    fill: ${({ theme }) => theme.colors.fog["80"]};
    opacity: 0;
    transition: 0.2s;
  }

  .Carousel2__tabButton {
    display: flex;
    align-items: center;
    margin: 0;
    padding: 0;
    text-align: left;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.subdued};
    background: transparent;
    transition: 0.2s;

    &:hover {
      color: ${({ theme }) => theme.colors.fog["60"]};

      .Carousel__arrowIcon {
        left: 12px;
        opacity: 0.8;
      }
    }
  }

  .Carousel2__tabButton:not(:first-child) {
    margin-top: 32px;
  }

  .Carousel2__tabButtonActive {
    color: ${({ theme }) => theme.colors.fog["80"]};

    /* stylelint-disable-next-line no-descending-specificity */
    .Carousel__arrowIcon {
      left: 12px;
      opacity: 1;
    }

    &:hover {
      color: ${({ theme }) => theme.colors.fog["80"]};

      .Carousel__arrowIcon {
        opacity: 1;
      }
    }
  }

  .Carousel2__tabs {
    position: relative;
    width: 640px;
    margin-left: 32px;
  }

  .Carousel2__tab {
    position: absolute;
    display: none;
  }

  .Carousel2__tab_active {
    position: static;
    display: block;
    animation: fade-in 200ms;
  }

  @keyframes fade-in {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Carousel__arrowIcon {
      width: 28px;
      height: 28px;
    }
  }
`;

export { Carousel2Desktop };
